import { useEffect, useState } from 'react';
import useClient from 'api/client';
import { useAlert } from 'components/message/AlertContext';
import { useParams } from 'react-router-dom';
import listDatasetTables from 'api/Dataset/listDatasetTables';
import syncTables from 'api/Dataset/syncTables';
import crawlDataset from 'api/Dataset/crawlDataset';
import { SearchFilter } from 'types/Filters';
import { DatasetTable } from 'types/Dataset';
import { PaginationItems } from 'types/Pagination';

export default function useTables(filter: SearchFilter) {
    const client = useClient();
    const { openAlert } = useAlert();
    const params = useParams<{ uri: string }>();
    const [loading, setLoading] = useState(true);
    const [crawlerLoading, setCrawlerLoading] = useState(false);
    const [error, setError] = useState(false);
    const [tables, setTables] = useState<DatasetTable[]>([]);
    const [showSearchbar, setShowSearchbar] = useState(false);
    const [items, setItems] = useState<PaginationItems>({
        count: 0,
        pages: 0,
        page: 0,
    });

    const synchronizeTables = async () => {
        setLoading(true);
        if (params.uri) {
            const response = await client.mutate(syncTables(params.uri));
            if (!response.errors) {
                setTables(response.data.syncTables.nodes);
            } else {
                openAlert({
                    message: `Failed to synchronize tables from Glue catalog. ${response.errors[0].message}`,
                    severity: 'alert',
                });
            }
        }
        setLoading(false);
    };

    const startCrawlingDataset = async () => {
        setCrawlerLoading(true);
        if (params.uri) {
            const response = await client.mutate(crawlDataset(params.uri));
            if (!response.errors) {
                openAlert({
                    message: 'Crawler Started',
                });
            } else {
                openAlert({
                    message: `Failed to crawl dataset. ${response.errors[0].message}`,
                    severity: 'alert',
                });
            }
        }
        setCrawlerLoading(false);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (params.uri) {
                try {
                    setLoading(true);
                    const response = await client.query(
                        listDatasetTables(params.uri, filter),
                    );
                    if (!response.errors) {
                        setTables(response.data.getDataset.tables.nodes);
                        setItems(response.data.getDataset.tables);

                        if (
                            response.data.getDataset.tables.count !== 0 &&
                            !showSearchbar
                        ) {
                            setShowSearchbar(true);
                        }
                    } else {
                        setError(true);
                    }
                } finally {
                    setLoading(false);
                }
            }
        };

        if (client) {
            fetchData();
        }
    }, [client, params.uri, filter]);

    return {
        tables,
        items,
        error,
        loading,
        showSearchbar,
        synchronizeTables,
        crawlerLoading,
        startCrawlingDataset,
    };
}
